import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, History } from "lucide-react";

/** Alta de un bono ya contratado antes de usar la plataforma: sin cobro ni factura. */
export const BonoHistoricoDialog = ({ open, onOpenChange, alumnoId, sedeId, onHecho }) => {
  const [planes, setPlanes] = useState([]);
  const [planId, setPlanId] = useState("");
  const [fecha, setFecha] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/planes").then(({ data }) => setPlanes(
      data.filter((p) => p.sede_id === sedeId && (p.clases_incluidas || 0) > 0)));
  }, [open, sedeId]);

  const plan = planes.find((p) => p.id === planId);
  const fin = plan && fecha
    ? new Date(new Date(fecha).setMonth(new Date(fecha).getMonth() + (plan.validez_meses || 12)))
      .toISOString().slice(0, 10)
    : "";

  const guardar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post(`/alumnos/${alumnoId}/bono-historico`,
        { plan_id: planId, fecha_inicio: fecha });
      toast.success(`Bono activado: ${data.clases_consumidas} clases dadas, `
        + `${data.clases_disponibles} disponibles hasta ${data.hasta}`, { duration: 9000 });
      onOpenChange(false);
      setPlanId("");
      setFecha("");
      onHecho?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="bono-historico-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Bono ya contratado</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <p className="text-sm text-muted-foreground">
            Para alumnos que compraron su bono antes de usar la plataforma. No genera cobro ni
            factura: solo carga el saldo de clases y las coberturas incluidas.
          </p>
          <div className="grid gap-2">
            <Label>Plan contratado</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger data-testid="bono-plan-select">
                <SelectValue placeholder="Elige el bono" />
              </SelectTrigger>
              <SelectContent>
                {planes.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.nombre} · {p.clases_incluidas} clases · {p.validez_meses || 12} meses
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Fecha de inicio (cuando lo contrató)</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
              data-testid="bono-fecha-input" />
          </div>
          {plan && fecha && (
            <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm"
              data-testid="bono-preview">
              <p><b>Vence el {fin}</b> ({plan.validez_meses || 12} meses)</p>
              <p className="text-muted-foreground mt-1">
                Se contarán automáticamente las clases a las que ya ha asistido desde esa fecha y el
                resto quedará como saldo disponible
                {plan.matricula ? " · matrícula incluida" : ""}
                {plan.seguro ? " · seguro de recuperación incluido" : ""}.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button className="rounded-full font-semibold" onClick={guardar}
            disabled={enviando || !planId || !fecha} data-testid="bono-historico-guardar">
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><History className="h-4 w-4 mr-2" /> Activar bono</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
